/* External */
import {
  Children,
  memo,
  useCallback, useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash-es/debounce';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import TabsList from './TabsList';

/* Utils */
import { Direction } from '../../utils/spatialNavigation';
import { noOp } from '../../utils/functions';
import { COMPONENTS } from '../../utils/focusKey';

/* Styles */
import './Tabs.scss';

const DEFAULT_ACTIVE_TAB = 0;

function Tabs({
  className,
  titleContent,
  disableFocus,
  children,
  setFocus,
  onScrolledChange,
  loadDefaultTab,
  showActiveTab,
  onArrowPress,
  focusDefaultTab,
}) {
  const [activeTab, setActiveTab] = useState(undefined);
  const [wasViewed, setWasViewed] = useState(false);
  const [wasFocused, setWasFocused] = useState(false);

  const handleTabBecameFocused = useCallback((id) => {
    onScrolledChange(true);
    setActiveTab(id);
  }, [onScrolledChange]);

  const handleArrowPress = useCallback((direction) => onArrowPress(direction) && (
    !(direction === Direction.RIGHT && activeTab === children.length - 1)
  ), [activeTab, children.length, onArrowPress]);

  const debounceSetActiveTab = useMemo(
    () => debounce((newActiveTab) => setActiveTab(newActiveTab), 500),
    [],
  );

  const handleTabBecameActive = useCallback((newActiveTab) => {
    if (newActiveTab !== activeTab) {
      setActiveTab(undefined);
      debounceSetActiveTab(newActiveTab);
    }
  }, [activeTab, debounceSetActiveTab]);

  const handleTabListFocused = useCallback((position, { focusKey }) => {
    if (!wasViewed && !wasFocused) {
      setWasFocused(true);
      setFocus(`${focusKey}_${DEFAULT_ACTIVE_TAB}`);
    }
  }, [setFocus, wasFocused, wasViewed]);

  useEffect(() => {
    if (loadDefaultTab && !activeTab) {
      setActiveTab(DEFAULT_ACTIVE_TAB);
    }
  }, [loadDefaultTab, activeTab]);

  useEffect(() => {
    if ((wasViewed || wasFocused) && focusDefaultTab) {
      setWasFocused(true);
      setFocus(`${COMPONENTS.TABS_LIST}_${DEFAULT_ACTIVE_TAB}`);
    }
  }, [focusDefaultTab, setFocus, wasFocused, wasViewed]);

  useEffect(() => {
    if (showActiveTab) {
      setWasViewed(true);
    }
  }, [showActiveTab]);

  useEffect(() => {
    setWasViewed(false);
    setWasFocused(false);
    setActiveTab(DEFAULT_ACTIVE_TAB);
  }, [titleContent]);

  const filteredChildren = Children
    .toArray(children)
    .filter((child) => !!child);

  return (
    <div className={`dtv-html5-c-tabs ${className}`}>
      <div className="dtv-html5-c-tabs__options">
        <h2 className="dtv-html5-c-tabs__title-content">{titleContent}</h2>
        <TabsList
          focusKey={COMPONENTS.TABS_LIST}
          focusable={!disableFocus}
          activeTab={activeTab}
          wasViewed={wasViewed}
          handleArrowPress={handleArrowPress}
          onTabBecameFocused={handleTabBecameFocused}
          onTabBecameActive={handleTabBecameActive}
          onScrolledChange={onScrolledChange}
          onBecameFocused={handleTabListFocused}
        >
          {filteredChildren}
        </TabsList>
      </div>
      <div className="dtv-html5-c-tabs__content">
        {filteredChildren
          .map((child, id) => {
            if (id !== activeTab) return undefined;
            return child;
          })}
      </div>
    </div>
  );
}

Tabs.propTypes = {
  className: PropTypes.string,
  titleContent: PropTypes.string,
  children: PropTypes.node.isRequired,
  onScrolledChange: PropTypes.func,
  onArrowPress: PropTypes.func,
  loadDefaultTab: PropTypes.bool,
  disableFocus: PropTypes.bool,
  showActiveTab: PropTypes.bool,
  setFocus: PropTypes.func,
  focusDefaultTab: PropTypes.bool,
};

Tabs.defaultProps = {
  className: '',
  titleContent: '',
  onScrolledChange: noOp,
  onArrowPress: noOp,
  loadDefaultTab: false,
  disableFocus: false,
  showActiveTab: false,
  setFocus: noOp,
  focusDefaultTab: false,
};

export default memo(withFocusable()(memo(Tabs)));
